/***************************************** Portal Globals *****************************/

// Centralized values + helpers shared across the subscriber portal (portal.js) and the
// portal upsell sections (portal-ppt-upsell.js / portal-tp-upsell.js). Loaded on all
// customer/portal pages BEFORE those scripts, so these top-level bindings are reachable
// by name from them (same pattern as products / handleToVariantID / root). Only one of
// portal.js / the upsell scripts loads per page, so their own names never collide here.

/********************* Recharge Bootstrap *********************/

// initialize the Recharge storefront client once (recharge-client loads before this file)
recharge.init({
    storefrontAccessToken: 'strfnt_383881b23003928358e7be0dad07b9118f2a096156434597cb023f03011c1938',
});

// shared recharge session + customer + customer-scoped pricing, populated during setup
let session = null;
let customer = null;
let subscriptionPrices = {};
let onetimePrices = {};

// shared customer account state, populated by portal.js / the upsell pages during setup.
// declared here once so portal.js and the upsell pages never redeclare them.
let shopifyCustomerID = null;
let subscriptions = [];
let addresses = [];
let paymentMethods = [];
let onetimes = [];

// portal product matrix (variant id -> product info) + handle lookup (handle -> variant id),
// loaded from /account?view=product-json by loadPortalProducts()
let products = {};
let productHandles = {};

// true once the welcome-discount eligibility check confirms the customer already used it
// (or when the check fails; we fail closed rather than promise a discount we can't verify)
let ineligibleForDiscount = false;

/********************* Utility Helpers *********************/

// convert a YYYY-MM-DD string to a human-readable date
function dateToString(dateString) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const [year, month, day] = dateString.split('-');
    const date = new Date(Date.UTC(year, month - 1, day));
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()} ${date.getUTCFullYear()}`;
}

// push a date object forward X days, returned as YYYY-MM-DD
function pushDate(date, days) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate.toISOString().split('T')[0];
}

// return a YYYY-MM-DD that is `days` after today in the customer's LOCAL calendar. Unlike building
// it from a UTC instant (Date.now() + 24h -> toISOString), this never rolls an extra day forward
// for evening orders in behind-UTC timezones, where "now" is already tomorrow in UTC.
function localDatePlus(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

// clear the session and send the customer back to login
function logout() {
    window.location.href = "/account/logout?return_url=%2Faccount%2Flogin";
}

// ship an error string to our API so we can investigate failures the customer never sees
async function logError(error) {
    await fetch(`${root}/portal/error.php`, {
        method: "POST",
        body: new URLSearchParams({
            error: error
        })
    });
}

/********************* Auth Bootstrap *********************/

// acquire the recharge session based on the page; returns the session or null (and handles the logged-out UX)
async function getPortalSession() {
    return await recharge.auth.loginShopifyAppProxy();
}

// recharge session tokens expire after 1 hour; the underlying Shopify app-proxy auth lasts days,
// so we silently re-mint the token just under the hour to keep long-open tabs from hitting
// "bad authentication" on the next action (most visibly on date reschedules)
const SESSION_REFRESH_INTERVAL_MS = 55 * 60 * 1000;

// start a background timer that refreshes the shared session before it expires
function startSessionRefresh() {
    setInterval(async () => {
        // re-mint the token; keep the existing session on failure so a transient blip doesn't log the customer out mid-task
        try {
            const refreshedSession = await getPortalSession();
            if (refreshedSession) {
                session = refreshedSession;
            }
        } catch (e) {
            await logError(`PORTAL-SESSION-REFRESH - ${e?.message ?? JSON.stringify(e)}`);
        }
    }, SESSION_REFRESH_INTERVAL_MS);
}

// fetch the recharge customer for the current session; returns the customer or null (handles the logged-out UX)
async function getPortalCustomer() {
    try {
        const portalCustomer = await recharge.customer.getCustomer(session);

        // identify the customer + track the portal login in klaviyo (best effort)
        try {
            klaviyo.identify({
                '$email': portalCustomer.email,
            })
                .then(() => {
                    klaviyo.push(["track", "Portal Login | MalterTech"]);
                });
        } catch (e) {
            // do nothing
        }

        return portalCustomer;
    } catch (e) {
        console.log(e);
        gtag('event', 'portal_error_login_customer');

        // distinguish "no account" from a transient access issue
        if (e.message === "Not logged in.") {
            await Swal.fire({
                icon: "warning",
                title: "This email is not associated with any current or past subscription.",
                html: `Please click <a href="/pages/plant-paper"><u>here</u></a> to set up a new subscription.`
            });
        } else {
            await Swal.fire({
                icon: "warning",
                title: "We're having an issue accessing your account.",
                text: "Please refresh and try again otherwise contact info@plantpaper.us with error code P1."
            });
        }

        logout();
        return null;
    }
}

/********************* Portal Products *********************/

// load the portal product matrix + handle lookup from the account product-json view.
// the portal can't render without it, so callers treat a failure like any other
// account-data failure (the P1 error path).
async function loadPortalProducts() {
    const response = await fetch("/account?view=product-json")
        .then(r => r.json());

    products = response.products;
    productHandles = response.handles;
    return products;
}

/********************* Pricing *********************/

// load the customer's subscription prices into `subscriptionPrices` (variant id -> cents)
async function loadSubscriptionPrices(shopifyCustomerID) {
    const response = await fetch(`${root}/portal/get-tiers.php`, {
        method: "POST",
        body: JSON.stringify({shopify_customer_id: shopifyCustomerID})
    })
        .then(r => r.json());

    // subscription prices come back in whole dollars; store cents to match our price() helper
    subscriptionPrices = {};
    Object.keys(response).forEach(variantID => {
        subscriptionPrices[variantID] = response[variantID] * 100;
    });
    return subscriptionPrices;
}

// load one-time (productSearch) prices into `onetimePrices` (variant id -> cents) for the given currency
async function loadOnetimePrices(currency) {
    const response = await recharge.product.productSearch(session, {
        limit: 250,
        format_version: '2022-06',
    });

    onetimePrices = {};
    (response.products ?? []).forEach(product => {
        // find the one-time selling plan so we can honor any Recharge one-time discount set on it
        const onetimePlanId = (product.plans ?? []).find(plan => plan.type === "onetime")?.id;

        (product.variants ?? []).forEach(variant => {
            const priceBlock = variant.prices?.[0];
            if (!priceBlock) {
                return;
            }
            // prefer the presentment price matching the customer's currency, else the default block
            const presentment = (priceBlock.presentment_prices ?? []).find(entry => entry.currency === currency);
            const priceData = presentment ?? priceBlock;
            // the one-time discount lives on the onetime plan's discounted_price; fall back to the full unit price
            const onetimePlan = (priceData.plans ?? []).find(plan => plan.id === onetimePlanId);
            const unitPrice = onetimePlan?.discounted_price ?? priceData.unit_price;
            onetimePrices[variant.external_variant_id] = Math.round(parseFloat(unitPrice) * 100);
        });
    });
    return onetimePrices;
}

// the customer's subscription price for a variant, in cents
function subscriptionPrice(variantID) {
    return subscriptionPrices[variantID];
}

// the customer's one-time price for a variant, in cents
function onetimePrice(variantID) {
    return onetimePrices[variantID];
}

/********************* Discount Codes *********************/

// canonical Recharge discount codes used across the portal + upsells
const DISCOUNT_CODES = {
    PPT_PORTAL_FIRST_CHARGE_25: "PPT_PORTAL_FIRST_CHARGE_25",
    PPT_PORTAL_FIRST_CHARGE_20: "PPT_PORTAL_FIRST_CHARGE_20",
    PPT_PORTAL_LIFETIME_10: "PPT_PORTAL_LIFETIME_10",
    PPT_WELCOME_20: "PPTWELCOME20",
    TP_WELCOME_20: "WELCOME20",
    TRYTISSUES: "TRYTISSUES",
    REACTIVATE_10: "REACTIVATE_10",
    SAVE5TWICE: "SAVE5TWICE",
};

/********************* Recharge Discount IDs *********************/

// A Recharge address's `discounts` only expose an `id` (no code/value), so we keep the
// ids for discounts we match by id. Regenerate with: php cli/discount-matrix.php (plantpaper-api)
const DISCOUNT_IDS = {
    PPT_PORTAL_LIFETIME_10: 297736767,
    SAVE5TWICE: 277837371,
};

/********************* Lifetime Discount Matrix *********************/

// discounts surfaced on a subscription as "Includes X% lifetime early subscriber discount".
// keyed by Recharge discount id -> { code, value }. Only PPT_PORTAL_LIFETIME_10 qualifies.
const LIFETIME_DISCOUNTS = {
    [DISCOUNT_IDS.PPT_PORTAL_LIFETIME_10]: {code: DISCOUNT_CODES.PPT_PORTAL_LIFETIME_10, value: 10},
};

// return the lifetime discount applied to an address (or null) by matching discount ids
function getLifetimeAddressDiscount(address) {
    const match = (address?.discounts ?? []).find(discount => LIFETIME_DISCOUNTS[discount.id]);
    if (!match) {
        return null;
    }
    return LIFETIME_DISCOUNTS[match.id];
}

/********************* Upsell Order Placement *********************/

// Places an upsell order end-to-end through Recharge, shared by both upsell pages. Steps:
//   1. clone the address (so any address-scoped discount stays isolated to this purchase)
//   2. create the subscription or onetime for the selected variant/product
//   3. poll until Recharge queues the charge
//   4. apply the optional charge-level discount to the queued charge
//   5. queue the optional address-level discount for the nightly cron
// The first charge is left queued for tomorrow (next_charge_scheduled_at) so it processes on
// the normal subscription cadence — we no longer charge the first order immediately.
// Returns { purchaseItemID, charge }. Throws on a fatal failure so the caller can show its
// error UI. Logged errors carry the variant id + the shared `customer`, so we can tell which
// product/page an order failed on without any extra arguments.
async function placeUpsellOrder({
                                    address,
                                    paymentMethodID,
                                    purchaseType,
                                    variantID,
                                    productID,
                                    cadence,
                                    chargeCode = null,
                                    addressCode = null,
                                }) {
    // tomorrow (local calendar), so the first charge is queued rather than processed instantly
    const nextChargeDate = localDatePlus(1);

    // always create a new address so permanent (address-scoped) discounts only apply to this purchase
    const newAddress = await recharge.address.createAddress(session, {
        first_name: address.first_name,
        last_name: address.last_name,
        address1: address.address1,
        address2: address.address2 ?? "",
        city: address.city,
        country_code: address.country_code,
        province: address.province,
        zip: address.zip,
        phone: address.phone,
        payment_method_id: paymentMethodID,
    });

    // either create a new subscription or onetime based on the customer's selection
    let purchaseItemID;
    if (purchaseType === "Subscribe") {
        const subResponse = await recharge.subscription.createSubscription(session, {
                address_id: newAddress.id,
                next_charge_scheduled_at: nextChargeDate,
                charge_interval_frequency: cadence,
                order_interval_frequency: cadence,
                order_interval_unit: 'week',
                quantity: 1,
                external_variant_id: {ecommerce: `${variantID}`},
                external_product_id: {ecommerce: `${productID}`},
            },
            {commit: true}
        );
        purchaseItemID = subResponse.id;
    } else {
        const otpResponse = await recharge.onetime.createOnetime(session, {
                address_id: newAddress.id,
                next_charge_scheduled_at: nextChargeDate,
                quantity: 1,
                external_variant_id: {ecommerce: `${variantID}`},
                external_product_id: {ecommerce: `${productID}`},
            },
            {commit: true}
        );
        purchaseItemID = otpResponse.id;
    }

    // sync the subscription to the customer's tiered price before any charge is processed;
    // createSubscription assigns the catalog price, not the tier price the customer was shown.
    // abort on failure rather than silently charging the wrong amount
    if (purchaseType === "Subscribe") {
        let refreshResponse;
        try {
            // form-encoded body: refresh-subscription-price.php reads $_POST, which PHP
            // only populates for urlencoded payloads (a JSON body arrives as an empty $_POST)
            refreshResponse = await fetch(`${root}/portal/refresh-subscription-price.php`, {
                method: "POST",
                body: new URLSearchParams({
                    subscriptionID: purchaseItemID
                })
            })
                .then(response => response.json());
        } catch (e) {
            refreshResponse = {error: e?.message ?? "request failed"};
        }
        if (refreshResponse.error) {
            await logError(`UPSELL-7 - refresh-subscription-price failed - customer=${customer?.id} variant=${variantID} purchaseItemID=${purchaseItemID} - ${refreshResponse.error}`);
            throw new Error(`UPSELL-7: could not refresh price for subscription ${purchaseItemID}`);
        }
    }

    // poll until Recharge generates the queued charge for our new sub/onetime
    let matchingCharge;
    let lastListError;
    for (let attempt = 0; attempt < 10; attempt++) {
        try {
            const chargeList = await recharge.charge.listCharges(session, {
                purchase_item_id: purchaseItemID,
            });
            matchingCharge = chargeList.charges?.[0];
            if (matchingCharge) {
                break;
            }
        } catch (e) {
            lastListError = e;
        }

        await sleep(1000);
    }

    // bail out if Recharge never produced a queued charge; the caller shows the error UI
    if (!matchingCharge) {
        throw new Error(lastListError
            ? `UPSELL-1: listCharges failed for variant=${variantID} purchaseItemID=${purchaseItemID} - ${lastListError?.message ?? JSON.stringify(lastListError)}`
            : `UPSELL-2: no queued charge found for variant=${variantID} purchaseItemID=${purchaseItemID}`);
    }

    // step 1: apply the charge-level discount to the queued charge so it's reflected when the
    // charge processes on its scheduled date. If the discount fails we abort (and roll back)
    // rather than silently charging the full price the customer was never shown.
    if (chargeCode) {
        try {
            await recharge.charge.applyDiscountToCharge(session, matchingCharge.id, chargeCode, {commit: true});
        } catch (e) {
            await logError(`UPSELL-3 - applyDiscountToCharge failed - customer=${customer?.id} variant=${variantID} purchaseItemID=${purchaseItemID} chargeID=${matchingCharge.id} code=${chargeCode} - ${e?.message ?? JSON.stringify(e)}`);
            throw new Error(`UPSELL-3: could not apply discount ${chargeCode} to charge ${matchingCharge.id}`);
        }
    }

    // step 2: queue the address-level discount for the nightly cron
    if (addressCode) {
        try {
            await fetch(`${root}/portal/apply-discount-code-to-subscription.php`, {
                method: "POST",
                body: JSON.stringify({
                    customer_id: customer.id,
                    subscription_id: purchaseItemID,
                    charge_id: matchingCharge.id,
                    discount_code: addressCode,
                }),
            });
        } catch (e) {
            await logError(`UPSELL-5 - address discount queue failed - customer=${customer?.id} variant=${variantID} subID=${purchaseItemID} code=${addressCode} - ${e?.message ?? JSON.stringify(e)}`);
        }
    }

    return {purchaseItemID, charge: matchingCharge};
}

/********************* Subscriber Currency *********************/

// the subscriber's currency ("USD" | "CAD"), resolved from their subscription and used by
// formatSubscriberPrice. Set by applySubscriberCurrency (upsells) or directly (portal.js).
let subscriberCurrency = "USD";

// symbol per supported currency. Both the US and Canada use "$", but resolving it from the
// subscriber's currency (not the storefront locale) keeps it correct if a non-$ market is ever
// added, and documents intent.
const CURRENCY_SYMBOLS = {
    USD: "$",
    CAD: "$",
};

// format a cent amount in the SUBSCRIBER's currency, independent of the storefront locale. Use this
// across the portal/upsells instead of price() (theme-global.js): price() prefixes the Liquid
// currency symbol from localization.country, which is wrong whenever a subscriber opens the portal
// from a different country than their account currency (e.g. a CAD subscriber on the US site).
function formatSubscriberPrice(amount) {
    const total = (parseFloat(amount) / 100).toFixed(2).replace(".00", "");
    return `${CURRENCY_SYMBOLS[subscriberCurrency] ?? "$"}${total}`;
}

// A subscriber's region is determined by their own Recharge subscriptions, never by storefront
// locale/geo (localization.country): that reflects where they're browsing and can disagree with
// their account, e.g. a CAD subscriber on a US connection ordering the US product. Mirrors
// portal.js, which keys all regional logic off presentment_currency. Prefer the currency of an
// active subscription; fall back to the most recent subscription so a paused/cancelled CAD
// subscriber is never mis-bucketed as USD. Requires `subscriptions` to be loaded first.
function getSubscriberCurrency() {
    const activeSubscription = subscriptions.find(subscription => subscription.status === "active");
    const referenceSubscription = activeSubscription ?? subscriptions[0];
    return referenceSubscription?.presentment_currency === "CAD" ? "CAD" : "USD";
}

// Both upsell pages render the USD and CAD product forms server-side (Liquid can't see the Recharge
// currency at render time). This removes every form that doesn't match the subscriber's currency so
// the wrong-currency variant can never be ordered. `formSelector` is the page's form class
// (e.g. ".portalPptUpsellForm"). Returns the resolved currency for one-time price loading.
function applySubscriberCurrency(formSelector) {
    subscriberCurrency = getSubscriberCurrency();
    document.querySelectorAll(formSelector).forEach(form => {
        if (form.dataset.currency !== subscriberCurrency) {
            form.remove();
        }
    });
    return subscriberCurrency;
}

/********************* Upsell Shared Helpers *********************/

// pull the customer's subscriptions/addresses/payments/onetimes into the shared globals.
// used by both upsell pages; portal.js has its own version that also loads pending upgrades
async function refreshUpsellGlobalVariables() {
    await Promise.all([
        recharge.subscription.listSubscriptions(session, {
            limit: 250,
            sort_by: 'id-desc',
        }),
        recharge.address.listAddresses(session),
        recharge.paymentMethod.listPaymentMethods(session),
        recharge.onetime.listOnetimes(session),
    ])
        .then(response => {
            subscriptions = response[0].subscriptions;
            addresses = response[1].addresses;
            paymentMethods = response[2].payment_methods;
            onetimes = response[3].onetimes;
        });
}

/********************* Duplicate Order Guard *********************/

// Customers occasionally place a second order for a product within a few days of a recent one,
// usually because the first order still showed "Processing" and they assumed it hadn't gone
// through (thread: "Duplicate Paper Towel Orders"). Before a NEW order is created on an upsell
// page we confirm intent. (The portal's "Ship My Order Now" / reschedule don't use this: they
// move the subscription's own upcoming charge rather than creating an additional order — which
// would also self-match here — and the portal's "order in process" notice + 72h disabled
// controls already cover that case.) Detection reads the customer's Recharge charges and counts
// BOTH completed and queued charges, because we now defer the first charge to the next day, so a
// just-placed order is an upcoming queued charge rather than a processed one.
const DUPLICATE_ORDER_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// return the customer's most recent charge containing `variantID` that counts as a potential
// duplicate, or null when there is none. A charge counts when it is either completed (processed
// in the last 7 days) or queued (scheduled in the next 7 days) — the latter catches the
// deferred first order. Normal far-future renewals are excluded. Fails open (returns null) so an
// API hiccup never blocks a legitimate order.
async function getRecentChargeForVariant(variantID) {
    if (variantID == null) {
        return null;
    }

    // narrow server-side to charges scheduled within +/- 7 days. This window captures both a
    // recently-processed charge (scheduled in the recent past) and the deferred first order
    // (queued for the near future), while excluding ancient orders and far-future renewals — so
    // we never page past the relevant charges regardless of sort order.
    let chargeList;
    try {
        chargeList = await recharge.charge.listCharges(session, {
            scheduled_at_min: pushDate(new Date(), -7),
            scheduled_at_max: pushDate(new Date(), 7),
            limit: 250,
        });
    } catch (e) {
        return null;
    }

    // within that window, match the same variant and a status that represents a real order
    // (completed or queued — Recharge statuses are lowercase)
    return (chargeList.charges ?? []).find(charge => {
        const status = (charge.status ?? "").toLowerCase();
        if (status !== "success" && status !== "queued") {
            return false;
        }
        return (charge.line_items ?? []).some(lineItem => {
            const lineVariant = lineItem.external_variant_id?.ecommerce ?? lineItem.external_variant_id;
            return String(lineVariant) === String(variantID);
        });
    }) ?? null;
}

// Shared confirmation popup for both duplicate-order guards below. Renders our own buttons
// inside html: (full control over styling, no swal2 button overrides) and can only be dismissed
// by choosing an option. Rendered inside any open <dialog> (e.g. the upsell confirm slider) so
// it stacks above it in the top layer; otherwise on the body. Returns true (proceed) / false
// (back out). The caller is responsible for any follow-up UI (e.g. closing the upsell dialog).
async function showDuplicateOrderPrompt(orderMessage, confirmText) {
    const swalTarget = document.querySelector("dialog[open]") ?? document.body;

    // GA4: surface how often a duplicate guard fires
    gtag('event', 'portal_duplicate_order_warning_shown');

    const confirmed = await new Promise(resolve => {
        Swal.fire({
            icon: "warning",
            title: "Did you mean to create an additional order?",
            html: `
                <div class="duplicateOrderText">${orderMessage}</div>
                <div class="duplicateOrderActions">
                    <button type="button" class="duplicateOrderConfirm">${confirmText}</button>
                    <button type="button" class="duplicateOrderDeny">No, I did not mean to create a duplicate order</button>
                </div>
            `,
            showConfirmButton: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            target: swalTarget,
            didOpen: () => {
                const popup = Swal.getPopup();
                popup.querySelector(".duplicateOrderConfirm").addEventListener("click", () => {
                    Swal.close();
                    resolve(true);
                });
                popup.querySelector(".duplicateOrderDeny").addEventListener("click", () => {
                    Swal.close();
                    resolve(false);
                });
            },
        });
    });

    gtag('event', confirmed ? 'portal_duplicate_order_confirmed' : 'portal_duplicate_order_cancelled');
    return confirmed;
}

// UPSELL guard: confirm before placing a NEW order for a product the customer already ordered in
// the last 7 days (recently processed) or has scheduled in the next 7 days (upcoming). Returns
// true (proceed) / false (back out). Show this BEFORE the order is created.
async function confirmDuplicateOrder(variantID) {
    const recentCharge = await getRecentChargeForVariant(variantID);

    // no recent/upcoming charge for this product — nothing to warn about, continue the flow
    if (!recentCharge) {
        return true;
    }

    // the upsell pages skip the product matrix during setup; load it so we can name the product
    if (Object.keys(products).length === 0) {
        try {
            await loadPortalProducts();
        } catch (e) {
            // fall back to a generic name rather than blocking the order
        }
    }

    // a completed charge was already processed (reference processed_at); a queued charge hasn't
    // processed yet — it's scheduled for an upcoming date (scheduled_at, formatted in UTC since
    // it's a date-only string)
    const productTitle = products[variantID]?.title ?? "your order";
    let orderMessage;
    if ((recentCharge.status ?? "").toLowerCase() === "queued") {
        const scheduledDate = new Date(recentCharge.scheduled_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
        });
        orderMessage = `You already have an order for <b>${productTitle}</b> scheduled to process on <b>${scheduledDate}</b>.`;
    } else {
        const processedDate = new Date(recentCharge.processed_at ?? recentCharge.created_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
        orderMessage = `We recently processed an order for <b>${productTitle}</b> on <b>${processedDate}</b>.`;
    }

    return showDuplicateOrderPrompt(orderMessage, `Yes, please process an additional shipment of ${productTitle}`);
}

// return the most recent COMPLETED charge for a single subscription processed within the last 7
// days, or null. Only completed charges are considered, so the upcoming charge being rescheduled
// is never counted (no self-match). Fails open (returns null) so an API hiccup never blocks.
async function getRecentProcessedOrderForSubscription(subscriptionID) {
    let chargeList;
    try {
        chargeList = await recharge.charge.listCharges(session, {
            purchase_item_id: subscriptionID,
            status: ["success"],
            processed_at_min: pushDate(new Date(), -7),
            limit: 10,
        });
    } catch (e) {
        return null;
    }

    const charges = chargeList.charges ?? [];
    if (charges.length === 0) {
        return null;
    }

    // most recently processed first
    charges.sort((a, b) => new Date(b.processed_at).getTime() - new Date(a.processed_at).getTime());
    return charges[0];
}

// PORTAL guard: confirm before moving a subscription's order up ("Ship My Order Now" / a near-
// date reschedule) when THAT subscription already had an order processed in the last 7 days.
// Returns true (proceed) / false (back out).
async function confirmShipNowDuplicate(subscription) {
    const recentCharge = await getRecentProcessedOrderForSubscription(subscription.id);

    // this subscription wasn't ordered recently — nothing to warn about
    if (!recentCharge) {
        return true;
    }

    const productTitle = products[subscription.external_variant_id?.ecommerce]?.title ?? "your order";
    const processedDate = new Date(recentCharge.processed_at ?? recentCharge.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
    const orderMessage = `We recently processed an order for <b>${productTitle}</b> on <b>${processedDate}</b>.`;

    return showDuplicateOrderPrompt(orderMessage, `Yes, please process an additional shipment of ${productTitle}`);
}

/********************* Upsell Shared Helpers *********************/

// check whether the customer can still use the welcome discount for the given flag
// (mt_eligible_tp_welcome / mt_eligible_ppt_welcome). Sets the shared
// `ineligibleForDiscount` and fails closed so we never promise a discount we can't confirm.
async function loadUpsellEligibility(eligibilityFlag) {
    try {
        const eligibilityResponse = await fetch(`${root}/portal/get-discount-eligibility.php`, {
            method: "POST",
            body: JSON.stringify({
                email: customer.email,
                customer_id: shopifyCustomerID
            })
        })
            .then(response => response.json());

        // mark ineligible when the customer has already used the welcome discount
        if (eligibilityResponse[eligibilityFlag] === false) {
            ineligibleForDiscount = true;
        }
    } catch (e) {
        console.error(e);
        ineligibleForDiscount = true;
    }
}
